import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppShell } from '@/components/layout/AppShell';
import {
  Alert,
  Button,
  Card,
  DataRow,
  ErrorState,
  OptionCard,
  RadioGroup,
  Skeleton,
  SkeletonGroup,
} from '@/components/ui';
import { DeliveryAddress } from '@/features/address/DeliveryAddress';
import { NEW_ADDRESS, type AddressPhase, type DeliverySubmit } from '@/features/address/model';
import { OtpForm } from '@/features/auth/OtpForm';
import type { PickedLocation } from '@/features/location/LocationPicker';
import type { LatLng } from '@/features/location/mapProvider';
import { useSession } from '@/features/auth/sessionContext';
import { useAnnounceOnChange } from '@/lib/a11y';
import { createAddress, updateAddress, useAddresses } from '@/lib/api/addresses';
import { useReverseGeocode } from '@/lib/api/geo';
import { queryKeys, useApiMutation } from '@/lib/api/queryClient';
import { createQuote, useOptionPrices, useQuote } from '@/lib/api/quotes';
import { documentsFromQuote, otherDocuments } from '@/features/pricing/basket';
import type { AddressOut, FulfilmentMethod, QuoteOut } from '@/lib/api/types';
import { formatPages, formatPaise, formatSheets } from '@/lib/format';

import { stepUrl } from './steps';
import { useOrderStep } from './useOrderStep';

const FORM_ID = 'address-form';

interface SubmitResult {
  quote: QuoteOut;
  address: AddressOut;
}

/**
 * /order/details — who you are, and where it goes.
 *
 * ONE SCREEN FOR BOTH, because they are one question. Signing in and giving a
 * delivery address used to be two steps, and the split was never about the
 * customer: addresses are kept against an account, so the number had to come
 * first, and that technical ordering became a screen of its own containing a
 * single field. A customer who is already signed in saw a page that asked
 * them nothing.
 *
 * So the number is asked for here, in place, and the rest of the screen
 * appears behind it. Nobody is sent to a login route and back, and the `?q=`
 * in the address bar is never dropped on the way.
 *
 * THE PIN IS THE ADDRESS. The carrier prices a JOURNEY between two points, so
 * the customer sets a POINT first and the fare comes back for that point.
 * What changed is what happens next: the point is also NAMED, by the server,
 * and the name carries the area, the city, the state and the PIN code. The
 * customer types the one line a map cannot know — the flat number — and
 * nothing they could get wrong. See DeliveryAddress for the two screens.
 *
 * The PIN code that prices the delivery and the PIN code saved on the
 * address are THE SAME VALUE from THE SAME SOURCE — the geocoder, or the
 * customer's own entry when the geocoder had none. Checkout refuses an order
 * where the two differ, so this is not tidiness; it is what keeps the order
 * placeable.
 *
 * Pressing Continue does two writes in order: save the address (it is the only
 * durable handle the payment step will have on it) and create the quote that
 * carries the point. The new quote id replaces `?q=` so the flow stays
 * recoverable from the URL alone.
 */

/**
 * The column is Numeric(9, 6), so anything finer than six decimals is noise
 * the database would round away anyway. Comparing at the stored precision
 * stops a float that differs in the twelfth place from counting as a move.
 */
function samePoint(a: number, b: number): boolean {
  return Math.round(a * 1e6) === Math.round(b * 1e6);
}

/** Great-circle distance, good enough to say "that is three kilometres away". */
function metresBetween(a: LatLng, b: LatLng): number {
  const toRad = (degrees: number) => (degrees * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6_371_000 * Math.asin(Math.sqrt(h));
}

/**
 * Write a moved pin back to a saved address, and return the address to order
 * against.
 *
 * Unmoved is the common case — a returning customer picks their home and
 * presses on — and it must cost no request at all.
 */
async function persistMovedPin(
  existing: AddressOut,
  location: PickedLocation,
): Promise<AddressOut> {
  const unchanged =
    existing.latitude != null &&
    existing.longitude != null &&
    samePoint(Number(existing.latitude), location.lat) &&
    samePoint(Number(existing.longitude), location.lng);
  if (unchanged) return existing;

  return updateAddress(existing.id, {
    latitude: location.lat,
    longitude: location.lng,
    location_source: location.source,
    ...(location.accuracyM !== null ? { location_accuracy_m: location.accuracyM } : {}),
  });
}

/**
 * What the customer is about to buy, shown while they hand over a number.
 *
 * It is reassurance, not navigation — the figure here is printing only, with
 * the line underneath saying so, because delivery cannot be priced until the
 * map below has a point on it.
 */
function OrderSoFar({ quote }: { quote: QuoteOut | undefined }) {
  if (!quote) return null;
  const single = quote.items.length === 1 ? quote.items[0] : undefined;

  return (
    <Card title="Your order so far">
      <dl>
        {quote.items.map((item, index) => (
          <DataRow
            key={item.upload_id}
            label={single ? 'Pages' : `Document ${index + 1}`}
            value={
              single
                ? formatPages(item.page_count)
                : `${formatPages(item.page_count)}, ${
                    item.copies === 1 ? '1 copy' : `${item.copies} copies`
                  }`
            }
          />
        ))}
        {single ? (
          <DataRow
            label="Copies"
            value={single.copies === 1 ? '1 copy' : `${single.copies} copies`}
          />
        ) : null}
        <DataRow label="Paper" value={formatSheets(quote.sheets)} />
        <DataRow label="Total so far" value={formatPaise(quote.total_paise)} emphasis />
      </dl>
      <p className="mt-2 text-sm text-ink-subtle">
        Delivery is added once you show us where it goes.
      </p>
    </Card>
  );
}

export function DetailsStep() {
  const { step, stepper, urlState } = useOrderStep();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useSession();

  const quote = useQuote(urlState.quote);
  const [selectedAddressId, setSelectedAddressId] = useState<string>(NEW_ADDRESS);
  // Component state, not the URL: a coordinate is not progress the customer
  // can lose without noticing, and a delivery address in a shareable link is
  // the one thing that should not be in one.
  const [location, setLocation] = useState<PickedLocation | null>(null);
  // Which of the two address screens is up. Here rather than in the form
  // because the action bar is a different button on each.
  const [phase, setPhase] = useState<AddressPhase>('point');
  // Where the device said the customer is, kept apart from the pin so the
  // screen can say how far they have dragged from it.
  const [deviceFix, setDeviceFix] = useState<LatLng | null>(null);
  // A PIN code typed because the map had none. Null when the map had one.
  const [pincodeOverride, setPincodeOverride] = useState<string | null>(null);

  // Delivery or collection. Seeded from the quote so a refresh — or coming back
  // from Pay — keeps the choice, then held here because changing it re-prices
  // and only the Continue button commits that.
  const [method, setMethod] = useState<FulfilmentMethod | null>(null);
  const fulfilmentMethod = method ?? quote.data?.fulfilment_method ?? 'delivery';
  const isPickup = fulfilmentMethod === 'pickup';

  // The whole basket, so the delivery quote covers every document: one parcel,
  // one courier rate, and the free-shipping threshold reads the lot.
  const basket = documentsFromQuote(quote.data);
  const firstItem = quote.data?.items[0];

  const signedIn = session.status === 'authenticated';
  const addresses = useAddresses(signedIn);
  const chosenSaved = addresses.data?.items.find((entry) => entry.id === selectedAddressId);

  // The name of the point. Asked only for a NEW address: a saved one already
  // carries its lines, and re-naming its pin would second-guess what the
  // customer confirmed last time.
  const geocode = useReverseGeocode(
    isPickup || chosenSaved || !location ? null : { lat: location.lat, lng: location.lng },
  );
  const named = geocode.data?.found ? geocode.data : null;

  // ONE PIN CODE, from one place. A saved address brings its own; a new one
  // takes the geocoder's, or the customer's when the geocoder had none.
  const pincode = isPickup ? null : (chosenSaved?.pincode ?? named?.pincode ?? pincodeOverride);

  const prices = useOptionPrices({
    uploadId: firstItem?.upload_id,
    copies: firstItem?.copies ?? 1,
    pincode,
    latitude: isPickup ? null : (location?.lat ?? null),
    longitude: isPickup ? null : (location?.lng ?? null),
    others: otherDocuments(basket, firstItem?.upload_id),
    fulfilmentMethod,
  });

  const priced = prices.data?.options.find(
    (entry) => entry.option.id === firstItem?.print_option_id,
  );
  const totalPaise = priced?.totals.total_paise ?? quote.data?.total_paise ?? null;

  // A delivery needs a point the carrier has priced; a collection needs
  // nothing at all. A price the carrier has actually quoted is the only one
  // we can hold the customer to, which is why `resolved` is checked rather
  // than merely having a location.
  const shippingKnown = location !== null && priced?.shipping_status === 'resolved';
  const canContinue = isPickup || shippingKnown;

  useAnnounceOnChange(
    isPickup && totalPaise !== null
      ? `Collecting from the shop. Total ${formatPaise(totalPaise)}, with no delivery charge.`
      : shippingKnown && totalPaise !== null
        ? `Delivered total ${formatPaise(totalPaise)}, including delivery.`
        : null,
  );

  const handleLocationChange = useCallback((next: PickedLocation) => {
    setLocation(next);
  }, []);
  // "Add a new address" clears the pin IN THE SAME EVENT, not in the effect
  // below. The map screen mounts on this very render, and it asks the device
  // for a fix only if it opens with no pin — opening with the saved
  // address's pin still in hand, cleared a moment later, left it waiting for
  // a drag that a customer who had just tapped "new" was not expecting to
  // make.
  const handleSelectAddress = useCallback((id: string) => {
    setSelectedAddressId(id);
    if (id === NEW_ADDRESS) {
      setLocation(null);
      setPhase('point');
    }
  }, []);
  // Memoised: both are reported from effects inside the form.
  const handleDeviceFix = useCallback((fix: LatLng) => setDeviceFix(fix), []);
  const handlePincodeOverride = useCallback((next: string | null) => setPincodeOverride(next), []);

  // Choosing a SAVED address adopts the pin it was saved with, so a returning
  // customer is not asked to place it again. ONLY WHEN THE CHOICE CHANGES,
  // never merely because the list arrived — see the git history of this
  // effect for the bug that rule fixed.
  // A returning customer lands on the address they usually use, not on an
  // empty map. Once, when the book first arrives, and only if they have not
  // already placed a pin THEMSELVES — their hand on the map beats our guess.
  // A device fix does not count: the map asks for one the moment it opens,
  // and it usually arrives before the address list does, so treating it as
  // the customer's choice would mean no returning customer ever saw their
  // saved address selected.
  //
  // AND NOT WHILE THE LIST IS STILL BEING FETCHED. Coming back from Pay, the
  // cache holds the list from BEFORE the address was saved — empty, for a
  // first-time customer — and it is handed over instantly while the fresh
  // one loads. Deciding on the stale copy would decide "nothing to select"
  // and never look again.
  const preselected = useRef(false);
  useEffect(() => {
    if (preselected.current || !addresses.data || addresses.isFetching) return;
    preselected.current = true;
    if (location?.source === 'map' || selectedAddressId !== NEW_ADDRESS) return;
    const usual = addresses.data.items.find((entry) => entry.is_default) ?? addresses.data.items[0];
    if (usual) setSelectedAddressId(usual.id);
  }, [addresses.data, addresses.isFetching, location, selectedAddressId]);

  const previousSelection = useRef<string | null>(null);
  useEffect(() => {
    const changed = previousSelection.current !== selectedAddressId;
    previousSelection.current = selectedAddressId;
    if (!changed) return;

    if (selectedAddressId === NEW_ADDRESS) {
      setLocation(null);
      setPhase('point');
      return;
    }
    const chosen = addresses.data?.items.find((entry) => entry.id === selectedAddressId);
    if (chosen?.latitude != null && chosen.longitude != null) {
      setLocation({
        lat: Number(chosen.latitude),
        lng: Number(chosen.longitude),
        source: chosen.location_source === 'device' ? 'device' : 'map',
        accuracyM: chosen.location_accuracy_m ?? null,
      });
    } else {
      setLocation(null);
    }
  }, [addresses.data, selectedAddressId]);

  const submit = useApiMutation<SubmitResult, DeliverySubmit>({
    mutationFn: async (values) => {
      // `canContinue` keeps the button disabled without one, so this is a
      // guard against a future edit rather than a case the customer can reach.
      if (!location) {
        throw new Error('A delivery address needs a location before it can be saved.');
      }

      const address = chosenSaved
        ? // A saved address whose pin the customer has just MOVED has to be
          // written back before checkout, because checkout stamps the order
          // from the address row, not from this screen.
          await persistMovedPin(chosenSaved, location)
        : await createAddress({
            name: values.name,
            phone: values.phone,
            line1: values.line1,
            ...(values.landmark ? { landmark: values.landmark } : {}),
            ...(values.label ? { label: values.label } : {}),
            ...(values.locality ? { locality: values.locality } : {}),
            city: values.city,
            state: values.state,
            pincode: values.pincode,
            latitude: location.lat,
            longitude: location.lng,
            location_source: location.source,
            ...(location.accuracyM !== null ? { location_accuracy_m: location.accuracyM } : {}),
          });

      // Re-quoted with the point and every document, and with THE SAME PIN
      // CODE just saved on the address — the pairing checkout checks.
      const repriced = await createQuote({
        documents: basket,
        fulfilment_method: 'delivery',
        pincode: values.pincode,
        latitude: location.lat,
        longitude: location.lng,
      });

      return { quote: repriced, address };
    },
    onSuccess: ({ quote: repriced, address }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.addresses });
      navigate(stepUrl('pay', { ...urlState, quote: repriced.id, address: address.id }));
    },
  });

  // A collection has nothing to fill in, so it gets its own submit: re-price
  // without a courier and go straight to payment.
  const collect = useApiMutation<QuoteOut, void>({
    mutationFn: () => createQuote({ documents: basket, fulfilment_method: 'pickup' }),
    onSuccess: (repriced) => {
      navigate(stepUrl('pay', { ...urlState, quote: repriced.id, address: undefined }));
    },
  });

  if (session.status === 'loading' || quote.isPending) {
    return (
      <AppShell title={step.title} progress={stepper}>
        <SkeletonGroup label="Loading your order">
          <div className="flex flex-col gap-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        </SkeletonGroup>
      </AppShell>
    );
  }

  // THE NUMBER FIRST, and nothing else until it is given. Delivery addresses
  // are kept against an account, so there is genuinely nothing to show below
  // this until we know whose account it is.
  if (!signedIn) {
    return (
      <AppShell
        title={step.title}
        subtitle="We need a number for delivery updates."
        progress={stepper}
        backTo={stepUrl('basket', urlState)}
        backLabel="Back to your files"
        wide
      >
        <div className="flex flex-col gap-4 lg:grid lg:grid-cols-2 lg:items-start lg:gap-6">
          <OtpForm />
          <OrderSoFar quote={quote.data} />
        </div>
      </AppShell>
    );
  }

  if (quote.isError && quote.error) {
    return (
      <AppShell title={step.title} progress={stepper} backTo={stepUrl('basket', urlState)}>
        <ErrorState
          error={quote.error}
          onRetry={() => void quote.refetch()}
          title="We could not load your price"
        />
      </AppShell>
    );
  }

  // Which screen the action bar belongs to. The map screen confirms a point;
  // every other state continues to payment.
  const choosingPoint = !isPickup && !chosenSaved && phase === 'point';
  const drift = deviceFix && location ? metresBetween(deviceFix, location) : 0;

  const pointSummary = (
    <div className="flex flex-col gap-2">
      <div className="min-w-0">
        {!location ? (
          <p className="text-sm text-ink-muted">Move the map until the pin is on your door.</p>
        ) : geocode.isFetching ? (
          <p className="text-sm text-ink-muted">Finding this place…</p>
        ) : named ? (
          <>
            <p className="truncate font-medium text-ink">
              {named.locality ?? named.city ?? 'Here'}
            </p>
            <p className="truncate text-sm text-ink-muted">{named.formatted}</p>
          </>
        ) : (
          <p className="text-sm text-ink-muted">Pin placed.</p>
        )}
        {/* Not an error: ordering from the office for home is normal. A nudge,
            because a pin this far from where you stand is also how a parcel
            goes to the wrong side of the city. */}
        {drift > 500 ? (
          <p className="mt-1 text-xs text-warning">
            The pin is{' '}
            {drift >= 1000 ? `${(drift / 1000).toFixed(1)} km` : `${Math.round(drift)} m`} from
            where you are.
          </p>
        ) : null}
      </div>
      {/* KEYED, and this is load-bearing. Confirm and Continue sit in the
          same slot of the same bar, and without distinct keys React reuses
          ONE <button> for both — updating its `type` to "submit" during the
          very click that confirmed the point. The browser then runs that
          click's default action against the node as it now is, and submits
          the form a customer has not yet seen: an error under the empty
          door-number box the instant it appears. A key makes it a new node,
          and a click finishes on the button it started on. */}
      <Button
        key="confirm-location"
        type="button"
        fullWidth
        disabled={!location || geocode.isFetching}
        onClick={() => setPhase('details')}
      >
        Confirm location
      </Button>
    </div>
  );

  const continueBar = (
    <div className="flex flex-col gap-2">
      {totalPaise !== null ? (
        <p className="flex items-baseline justify-between text-sm">
          <span className="text-ink-muted">
            {isPickup
              ? 'Total to pay'
              : shippingKnown
                ? 'Delivered total'
                : 'Total before delivery'}
          </span>
          <span className="text-lg font-semibold tabular-nums text-ink">
            {formatPaise(totalPaise)}
          </span>
        </p>
      ) : null}
      {isPickup ? (
        <Button
          key="collect"
          type="button"
          fullWidth
          onClick={() => collect.mutate()}
          loading={collect.isPending}
          loadingText="Saving your choice…"
        >
          Continue to payment
        </Button>
      ) : (
        <Button
          key="continue-to-payment"
          type="submit"
          form={FORM_ID}
          fullWidth
          disabled={!canContinue}
          loading={submit.isPending}
          loadingText="Saving your address…"
        >
          {location === null
            ? 'Set your location to continue'
            : !shippingKnown
              ? 'Working out delivery…'
              : 'Continue to payment'}
        </Button>
      )}
    </div>
  );

  return (
    <AppShell
      title={step.title}
      subtitle={
        isPickup
          ? 'Collect it from the shop. Nothing to fill in.'
          : choosingPoint
            ? 'Put the pin on your door. We work out delivery from the exact spot.'
            : 'Nearly there — just the door number.'
      }
      progress={stepper}
      backTo={stepUrl('basket', urlState)}
      backLabel="Back to your files"
      action={choosingPoint ? pointSummary : continueBar}
    >
      <div className="flex flex-col gap-4">
        {submit.isError && submit.error ? (
          <Alert severity="danger" title="We could not save that" live>
            <p>{submit.error.message}</p>
          </Alert>
        ) : null}

        {collect.isError && collect.error ? (
          <Alert severity="danger" title="We could not save that" live>
            <p>{collect.error.message}</p>
          </Alert>
        ) : null}

        {/* Before anything else on the screen: it decides whether there is an
            address to fill in at all, and whether delivery is being charged. */}
        <RadioGroup legend="How would you like to get it?">
          <OptionCard
            name="fulfilment"
            value="delivery"
            checked={!isPickup}
            onChange={() => setMethod('delivery')}
            label="Deliver to me"
            description="By two-wheeler, across Kochi. Priced from the exact spot you pick."
            compact
          />
          <OptionCard
            name="fulfilment"
            value="pickup"
            checked={isPickup}
            onChange={() => setMethod('pickup')}
            label="Collect from the shop"
            description="No delivery charge. We will tell you when it is ready."
            compact
          />
        </RadioGroup>

        {isPickup ? (
          <Alert severity="info" title="We will tell you when it is ready">
            <p>
              Your order will be printed and held at the counter. You will see it move to
              &ldquo;Ready to collect&rdquo; on your orders page, and we will call you on the number
              on your account.
            </p>
          </Alert>
        ) : (
          <DeliveryAddress
            formId={FORM_ID}
            saved={addresses.data?.items ?? []}
            savedLoading={addresses.isLoading}
            selectedAddressId={selectedAddressId}
            onSelectAddress={handleSelectAddress}
            location={location}
            onLocationChange={handleLocationChange}
            geocode={{
              data: geocode.data,
              isFetching: geocode.isFetching,
              isError: geocode.isError,
            }}
            phase={phase}
            onPhaseChange={setPhase}
            onDeviceFix={handleDeviceFix}
            onPincodeOverride={handlePincodeOverride}
            onValidSubmit={(values) => submit.mutate(values)}
            serverFieldErrors={submit.error?.fieldErrors ?? {}}
            disabled={submit.isPending}
            defaultPhone={session.user?.phone ?? ''}
            defaultName={session.user?.name ?? ''}
          />
        )}

        {/* The itemised breakdown lives on the Pay screen, which is where a
            customer decides. Here the sticky bar's total is enough; a card
            and a table under the form made the page three screens long. */}
        {!choosingPoint && !isPickup && prices.isFetching && !priced ? (
          <SkeletonGroup label="Working out delivery">
            <Skeleton className="h-10 w-full" />
          </SkeletonGroup>
        ) : null}
      </div>
    </AppShell>
  );
}
